import { CONST } from "@/constants"

export abstract class PriceSharedUtils {
  static getCentsPrice(price: number): number {
    return Math.round(price * 100)
  }

  static getDollarsPrice(cents: number): number {
    return Number((cents / 100).toFixed(2))
  }

  static calculateGST(amount: number): number {
    return amount * CONST.GST_RATE
  }

  static calculateHST(amount: number): number {
    return amount * CONST.HST_RATE
  }

  static calculateTip(amount: number, tipRate: number): number {
    return amount * tipRate
  }

  static calculateApplicationFeeAmount({
    amountCents,
    applicationFeeRate,
  }: {
    amountCents: number
    applicationFeeRate: number
  }) {
    const cents = Math.round(amountCents * applicationFeeRate)
    return {
      cents,
      dollars: PriceSharedUtils.getDollarsPrice(cents),
    }
  }

  static computeEffectivePriceCents(priceCents: number, discount: number): number {
    const effectivePriceCents = priceCents * (1 - discount / 100)
    if (effectivePriceCents > priceCents) {
      return priceCents
    }
    return Math.round(effectivePriceCents)
  }

  static calculateTotalAmountsCents(
    data: {
      priceCents: number
      discount: number
      quantity: number
      addOns: {
        priceCents: number
        quantity: number
        discount: number
      }[]
    }[],
    priceType: "effectivePrice" | "price"
  ) {
    if (priceType === "effectivePrice") {
      return data.reduce((acc, item) => {
        const dishEffectivePriceCents = PriceSharedUtils.computeEffectivePriceCents(item.priceCents, item.discount)
        const addOnsEffectivePriceCents = item.addOns.reduce((acc2, addOn) => {
          return acc2 + PriceSharedUtils.computeEffectivePriceCents(addOn.priceCents, addOn.discount) * addOn.quantity
        }, 0)
        return acc + (dishEffectivePriceCents + addOnsEffectivePriceCents) * item.quantity
      }, 0)
    }
    return data.reduce((acc, item) => {
      const dishPriceCents = item.priceCents
      const addOnsPriceCents = item.addOns.reduce((acc2, addOn) => {
        return acc2 + addOn.priceCents * addOn.quantity
      }, 0)
      return acc + (dishPriceCents + addOnsPriceCents) * item.quantity
    }, 0)
  }

  static calculateStripeFeeAmountCents({ totalAmountWithTipAndTaxCents }: { totalAmountWithTipAndTaxCents: number }) {
    const cents = Math.round(totalAmountWithTipAndTaxCents * CONST.STRIPE_FEE_RATE) + CONST.STRIPE_FEE_BASE_AMOUNT_CENTS
    // We need to perform the calculation off constants because dividing the total amount by 2 is not accurate because of precision loss
    const customerStripeFeeAmount =
      Math.round(totalAmountWithTipAndTaxCents * CONST.STRIPE_FEE_RATE_CUSTOMER) +
      CONST.STRIPE_FEE_BASE_AMOUNT_CENTS_CUSTOMER
    const merchantStripeFeeAmount = cents - customerStripeFeeAmount

    return {
      cents,
      dollars: PriceSharedUtils.getDollarsPrice(cents),
      customerCents: customerStripeFeeAmount,
      merchantCents: merchantStripeFeeAmount,
      customerDollars: PriceSharedUtils.getDollarsPrice(customerStripeFeeAmount),
      merchantDollars: PriceSharedUtils.getDollarsPrice(merchantStripeFeeAmount),
    }
  }

  static calculatePaymentAmounts({
    totalEffectiveAmountCents,
    tipRate,
    couponMerchant,
  }: {
    totalEffectiveAmountCents: number
    tipRate: number
    couponMerchant?: boolean
  }) {
    const gstAmountCents = PriceSharedUtils.calculateGST(totalEffectiveAmountCents)
    const hstAmountCents = PriceSharedUtils.calculateHST(totalEffectiveAmountCents)
    const tipAmountCents = Math.round(PriceSharedUtils.calculateTip(totalEffectiveAmountCents, tipRate))
    const totalEffectiveAmountWithTaxCents = Math.round(totalEffectiveAmountCents + gstAmountCents + hstAmountCents)
    const totalAmountWithTipAndTaxCents = Math.round(totalEffectiveAmountWithTaxCents + tipAmountCents)

    const customerStripeFeeAmountCents = PriceSharedUtils.calculateStripeFeeAmountCents({
      totalAmountWithTipAndTaxCents,
    })

    const stripeFees = couponMerchant ? 0 : customerStripeFeeAmountCents.customerCents
    const totalAmountToPayCents = totalAmountWithTipAndTaxCents + stripeFees

    return {
      cents: {
        totalAmountWithTipAndTax: totalAmountWithTipAndTaxCents,
        totalEffectiveAmountWithTax: totalEffectiveAmountWithTaxCents,
        tipAmount: tipAmountCents,
        gstAmount: gstAmountCents,
        hstAmount: hstAmountCents,
        stripeFeeAmount: customerStripeFeeAmountCents,
        totalAmountToPay: totalAmountToPayCents,
      },
      dollars: {
        totalAmountWithTipAndTax: PriceSharedUtils.getDollarsPrice(totalAmountWithTipAndTaxCents),
        totalEffectiveAmountWithTax: PriceSharedUtils.getDollarsPrice(totalEffectiveAmountWithTaxCents),
        tipAmount: PriceSharedUtils.getDollarsPrice(tipAmountCents),
        gstAmount: PriceSharedUtils.getDollarsPrice(gstAmountCents),
        hstAmount: PriceSharedUtils.getDollarsPrice(hstAmountCents),
        stripeFeeAmount: {
          customer: PriceSharedUtils.getDollarsPrice(customerStripeFeeAmountCents.customerCents),
          merchant: PriceSharedUtils.getDollarsPrice(customerStripeFeeAmountCents.merchantCents),
        },
        totalAmountToPay: PriceSharedUtils.getDollarsPrice(totalAmountToPayCents),
      },
    }
  }
}
